import { faker } from "./seed";
import { DEMO_ADVERTISER_ID } from "../config";
import { daysAgo, isoDate, isoTimestamp } from "./dates";
import { statusById } from "./creativeStatuses";

const AUDIO_TYPE_CATALOG = [
  { display_name: "Podcasts", id: "528", type: "inventory_type", value: "3", clusivity: "include" },
  { display_name: "Music Streaming Service", id: "476", type: "inventory_type", value: "1", clusivity: "include" },
  { display_name: "FM/AM Broadcast", id: "477", type: "inventory_type", value: "2", clusivity: "include" },
];

const SCRIPTS = [
  "iPhone 16. The most advanced chip ever in a smartphone — A18 Pro. All-new camera controls, 4K 120fps video, and a battery that lasts all day. iPhone 16. Get yours at apple.com.",
  "Meet Apple Intelligence. The personal intelligence system built into iPhone, iPad, and Mac. It understands you, works across your apps, and keeps your data private by design. Only on Apple.",
  "AirPods Pro with active noise cancellation. Hear only what matters. Adaptive Audio. Personalized Spatial Audio. Up to 30 hours battery. AirPods Pro — available now at apple.com.",
  "Apple Watch Series 10. The thinnest Apple Watch ever. ECG, crash detection, sleep apnea notifications. The watch that watches over you. Apple Watch Series 10 at apple.com.",
  "MacBook Pro with M4. Up to 24 core GPU, up to 24 hours battery life. Built for pros who refuse to wait. MacBook Pro — now at apple.com.",
  "Apple One. Six incredible services. One simple subscription. Apple Music, Apple TV+, Apple Arcade, iCloud+, Apple Fitness+, and Apple News+. Try it free for one month.",
];

const CREATIVES_META = [
  { name: "iPhone 16 — Holiday 30s", labels: ["iphone", "holiday"], status_id: 1, duration: 30, scriptIdx: 0 },
  { name: "Apple Intelligence — Brand 60s", labels: ["brand", "ai"], status_id: 1, duration: 60, scriptIdx: 1 },
  { name: "AirPods Pro — Q2 Push", labels: ["airpods", "promo"], status_id: 1, duration: 30, scriptIdx: 2 },
  { name: "Apple Watch Series 10 — Health", labels: ["watch", "health"], status_id: 1, duration: 45, scriptIdx: 3 },
  { name: "MacBook Pro M4 — Back to School", labels: ["mac", "back-to-school"], status_id: 3, duration: 30, scriptIdx: 4 },
  { name: "Apple One — Subscription Launch", labels: ["services", "bundle"], status_id: 1, duration: 30, scriptIdx: 5 },
];

function buildSeed() {
  return CREATIVES_META.map((meta, i) => {
    const id = 5000 + i;
    const status = statusById(meta.status_id);
    const variationCount = faker.number.int({ min: 1, max: 3 });
    const variations = Array.from({ length: variationCount }, (_, vi) => {
      const vStatusId = vi === 0 ? meta.status_id : faker.helpers.arrayElement([1, 3]);
      const vStatus = statusById(vStatusId);
      return {
        id: id * 10 + vi + 1,
        creative_id: id,
        asset_id: 90000 + id * 10 + vi + 1,
        asset_uuid: faker.string.uuid(),
        labels: vi === 0 ? ["original"] : ["variant", String.fromCharCode(64 + vi + 1)],
        url: `https://demo.audiohook.com/mock-audio/${id}-${vi + 1}.mp3`,
        status_id: vStatusId,
        creative_statuses: { name: vStatus.name, color: vStatus.color },
        created_at: isoTimestamp(daysAgo(faker.number.int({ min: 1, max: 50 }))),
        updated_at: isoTimestamp(daysAgo(1)),
      };
    });
    return {
      id,
      advertiser_id: DEMO_ADVERTISER_ID,
      name: meta.name,
      labels: meta.labels,
      status_id: meta.status_id,
      duration: meta.duration,
      script: SCRIPTS[meta.scriptIdx],
      start_date: isoDate(daysAgo(faker.number.int({ min: 30, max: 60 }))),
      end_date: isoDate(daysAgo(-30)),
      audio_types: faker.helpers.arrayElements(AUDIO_TYPE_CATALOG, { min: 1, max: 2 }),
      created_at: isoTimestamp(daysAgo(60 - i * 5)),
      updated_at: isoTimestamp(daysAgo(1)),
      creative_statuses: { name: status.name, color: status.color },
      creative_variations: variations,
    };
  });
}

let rows = null;
let variationRows = null;
let cachedDate = null;

function init() {
  const today = new Date().toISOString().slice(0, 10);
  if (rows && cachedDate === today) return;
  cachedDate = today;
  rows = buildSeed();
  variationRows = rows.flatMap((c) => c.creative_variations);
}

export function getCreatives() {
  init();
  return rows;
}

export function getCreativeVariations() {
  init();
  return variationRows;
}

export function updateCreative(patch, filterFn) {
  init();
  const updatedAt = new Date().toISOString();
  rows = rows.map((r) => {
    if (!filterFn(r)) return r;
    const updated = { ...r, ...patch, updated_at: updatedAt };
    // Keep the embedded creative_statuses in sync if status_id changed
    if (patch.status_id != null) {
      const s = statusById(patch.status_id);
      updated.creative_statuses = s ? { name: s.name, color: s.color } : r.creative_statuses;
    }
    return updated;
  });
  // Rebuild flat variations from updated rows
  variationRows = rows.flatMap((c) => c.creative_variations);
}

export function updateCreativeVariation(patch, filterFn) {
  init();
  const updatedAt = new Date().toISOString();
  rows = rows.map((c) => ({
    ...c,
    creative_variations: c.creative_variations.map((v) => {
      if (!filterFn(v)) return v;
      const updated = { ...v, ...patch, updated_at: updatedAt };
      if (patch.status_id != null) {
        const s = statusById(patch.status_id);
        updated.creative_statuses = s ? { name: s.name, color: s.color } : v.creative_statuses;
      }
      return updated;
    }),
  }));
  variationRows = rows.flatMap((c) => c.creative_variations);
}

export function insertCreative(body) {
  init();
  const id = Date.now();
  const statusId = body.status_id ?? 3;
  const status = statusById(statusId);
  const newRow = {
    id,
    creative_variations: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ...body,
    status_id: statusId,
    creative_statuses: status ? { name: status.name, color: status.color } : null,
  };
  rows = [newRow, ...rows];
  variationRows = rows.flatMap((c) => c.creative_variations);
  return newRow;
}

export function deleteCreative(filterFn) {
  init();
  rows = rows.filter((r) => !filterFn(r));
  variationRows = rows.flatMap((c) => c.creative_variations);
}
